import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { SearchOutlined } from '@ant-design/icons';
import { Button, Input, Checkbox, Spin, Icon } from 'antd';
import debounce from 'lodash/debounce';

const antIcon = <Icon type="loading" style={{ fontSize: 16 }} spin />;

export default class PropertySelectorModal extends React.Component {
  constructor(props) {
    super(props);

    const { properties, propertiesSelected, propertiesSlugsExcluded } = props;
    this.state = {
      propertiesSelected: propertiesSelected || [],
      properties: this.initProperties(properties, propertiesSelected, propertiesSlugsExcluded),
    };
    this.propertiesListRef = null;

    this.handleSearchChange = debounce(this.handleSearchChange.bind(this), 300);
  }

  componentDidMount() {
    this.propertiesListRef.addEventListener('scroll', this.handleScrollToBottom);
  }

  componentWillUnmount() {
    this.propertiesListRef.removeEventListener('scroll', this.handleScrollToBottom);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.properties !== this.props.properties) {
      const { properties, propertiesSlugsExcluded } = nextProps;

      this.setState({
        properties: this.initProperties(
          properties,
          this.state.propertiesSelected,
          propertiesSlugsExcluded,
        ),
      });
    }
  }

  handleScrollToBottom = () => {
    const { scrollHeight, clientHeight, scrollTop } = this.propertiesListRef;
    const toBottom = scrollHeight - scrollTop - clientHeight;
    if (toBottom <= 100) {
      this.props.onFetchData();
    }
  };

  handleSearchChange = e => {
    this.props.onSearch?.(e.target.value);
  };

  initProperties = (properties, propertiesSelected, propertiesSlugsExcluded) => {
    if (!properties) return [];
    const result = properties.map((property, index) => {
      const selected = !!propertiesSelected.find(item => {
        return item.slug === property.node.slug;
      });
      const disabled = propertiesSlugsExcluded.includes(property?.node?.slug);
      return {
        ...property.node,
        index,
        selected,
        disabled,
      };
    });
    return result;
  };

  handleSelect = (id, checked) => {
    const { properties, propertiesSelected } = this.state;
    const targetProperty = properties.find(property => property.id === id);
    if (targetProperty) {
      const obj = {
        slug: targetProperty.slug,
        name: targetProperty.name,
        id: targetProperty.id,
      };
      targetProperty.selected = checked;
      if (checked && propertiesSelected.indexOf(obj) === -1) {
        propertiesSelected.push(obj);
      } else if (!checked) {
        propertiesSelected.splice(propertiesSelected.indexOf(obj), 1);
      }
      this.setState({
        properties,
        propertiesSelected,
      });
    }
  };

  handleConfirm = () => {
    this.props.onSubmit(this.state.propertiesSelected);
  };

  render() {
    const { t, loading, isOnTop } = this.props;
    return (
      <div
        className={classNames('property-selector-modal', {
          'property-selector-modal--top': isOnTop,
        })}
      >
        <div className="property-selector-modal__search">
          <Input
            placeholder={t('cms.select_property.search')}
            suffix={<SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
            onChange={this.handleSearchChange}
          />
        </div>
        <div
          className="property-selector-modal__list"
          ref={node => {
            this.propertiesListRef = node;
          }}
        >
          <Choose>
            <When condition={loading}>
              <div className="property-selector-modal__loading" />
            </When>
            <Otherwise>
              <Choose>
                <When condition={this.state.properties}>
                  <For each="property" of={this.state.properties} index="idx">
                    <Checkbox
                      className="property-selector-modal__item"
                      checked={property.selected}
                      disabled={property.disabled}
                      onChange={e => {
                        this.handleSelect(property.id, e.target.checked);
                      }}
                      key={property.id}
                    >
                      {property.name}
                    </Checkbox>
                  </For>
                  <If condition={this.props.fetchLoading}>
                    <div className="property-selector-modal__fetch-loading">
                      <Spin indicator={antIcon} />
                    </div>
                  </If>
                </When>
                <Otherwise>
                  <div className="property-selector-modal__no-result">
                    {t('cms.select_property.no_result')}
                  </div>
                </Otherwise>
              </Choose>
            </Otherwise>
          </Choose>
        </div>
        <div className="property-selector-modal__footer">
          <If condition={this.state.properties && Object.keys(this.state.properties).length > 0}>
            <Button size="small" type="primary" onClick={this.handleConfirm}>
              {t('cms.select_property.confirm')}
            </Button>
          </If>
        </div>
      </div>
    );
  }
}

PropertySelectorModal.propTypes = {
  properties: PropTypes.array,
  propertiesSelected: PropTypes.array,
  propertiesSlugsExcluded: PropTypes.array,
  fetchLoading: PropTypes.bool,
  t: PropTypes.func,
  isOnTop: PropTypes.bool,
  onSubmit: PropTypes.func,
  onlyOneCountry: PropTypes.bool,
  onSearch: PropTypes.func,
};

PropertySelectorModal.defaultProps = {
  t: () => {},
  properties: [],
  propertiesSelected: [],
  propertiesSlugsExcluded: [],
  fetchLoading: false,
  isOnTop: false,
  onSubmit: () => {},
  onlyOneCountry: true,
  onSearch: () => {},
};
